// ✅ src/lib/auth.js (FULL - UPDATED, SAFE)
// ✅ Keeps: persistence logic, email auth helpers, redirect handler (still safe)
// ✅ FIX: Google login NO LONGER auto-falls back to redirect on iOS / in-app / blocked sessionStorage
// ✅ Instead: throws a friendly error telling user to open in Safari/Chrome and allow popups
// ✅ Returns { user, isNewUser } for Google + Email signup so you can route new users to /choose

import { auth } from "./firebase";
import {
  GoogleAuthProvider,
  signInWithPopup,
  signInWithRedirect,
  getRedirectResult,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  sendEmailVerification,
  reload,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";

const googleProvider = new GoogleAuthProvider();

/* ---------------- persistence ---------------- */

async function ensurePersistence() {
  try {
    await setPersistence(auth, browserLocalPersistence);
    return "local";
  } catch {
    try {
      await setPersistence(auth, browserSessionPersistence);
      return "session";
    } catch {
      return "none";
    }
  }
}

/* ---------------- environment checks ---------------- */

function canUseSessionStorage() {
  try {
    sessionStorage.setItem("__t", "1");
    sessionStorage.removeItem("__t");
    return true;
  } catch {
    return false;
  }
}

function isIOS() {
  try {
    const ua = navigator.userAgent || "";
    return /iPhone|iPad|iPod/i.test(ua);
  } catch {
    return false;
  }
}

// In-app browsers (WhatsApp/Instagram/Facebook/Twitter etc.) often break storage for redirect flows
function isInAppBrowser() {
  try {
    const ua = navigator.userAgent || "";
    return /FBAN|FBAV|Instagram|Line|Twitter|WhatsApp|wv/i.test(ua);
  } catch {
    return false;
  }
}

function stamp(tag, extra = {}) {
  try {
    // keep your debug checks but do not log
    const hasLocalStorage = (() => {
      try {
        localStorage.setItem("__t", "1");
        localStorage.removeItem("__t");
        return true;
      } catch {
        return false;
      }
    })();

    const hasSessionStorage = canUseSessionStorage();

    void tag;
    void extra;
    void hasLocalStorage;
    void hasSessionStorage;
  } catch {
    // ignore
  }
}

/**
 * ✅ Call once on app startup
 */
export async function initAuth() {
  stamp("[AUTH] initAuth() start");
  const mode = await ensurePersistence();
  stamp("[AUTH] initAuth() done", { mode });
  return mode;
}

/**
 * ✅ Google login — popup first
 * ✅ Redirect fallback ONLY when sessionStorage is usable and not iOS/in-app
 * ✅ RETURNS:
 *   - Popup flow: { user, isNewUser }
 *   - Redirect flow started: null (because page navigates away)
 */
export async function loginWithGoogle() {
  stamp("[AUTH] loginWithGoogle() start");

  const mode = await ensurePersistence();
  stamp("[AUTH] ensurePersistence() mode", { mode });

  googleProvider.setCustomParameters({ prompt: "select_account" });

  // ✅ Extra safety: if sessionStorage is blocked, redirect WILL fail with "missing initial state"
  const storageOk = canUseSessionStorage();
  const ios = isIOS();
  const inApp = isInAppBrowser();

  try {
    const res = await signInWithPopup(auth, googleProvider);
    stamp("[AUTH] popup success", { uid: res?.user?.uid || null });

    return {
      user: res?.user || null,
      isNewUser: !!res?.additionalUserInfo?.isNewUser,
    };
  } catch (e) {
    // 🚫 Don't auto-redirect in environments where redirect commonly breaks
    if (!storageOk || ios || inApp) {
      throw new Error(
        "Google sign-in failed in this browser. Please open the site in Safari/Chrome (not WhatsApp/Instagram browser), turn off Private Browsing, and allow popups, then try again."
      );
    }

    // ✅ Only then do redirect fallback
    try {
      sessionStorage.setItem("debug_redirect_started", String(Date.now()));
    } catch {}

    stamp("[AUTH] BEFORE redirect (fallback)");
    await signInWithRedirect(auth, googleProvider);

    // redirect happens; no user yet
    return null;
  }
}

/**
 * ✅ Handle redirect result
 * ✅ RETURNS:
 *   - null if no redirect result
 *   - { user, isNewUser } if redirect completed
 */
export async function handleGoogleRedirect() {
  try {
    stamp("[AUTH] handleGoogleRedirect() start");

    // If sessionStorage is blocked, redirect result processing is unreliable — just return null
    if (!canUseSessionStorage()) return null;

    try {
      sessionStorage.getItem("debug_redirect_started");
    } catch {}

    const result = await getRedirectResult(auth);

    if (!result?.user) {
      return null;
    }

    stamp("[AUTH] redirect success", { uid: result.user.uid });

    return {
      user: result.user,
      isNewUser: !!result?.additionalUserInfo?.isNewUser,
    };
  } catch {
    return null;
  }
}

/* ---------------- Email auth ---------------- */

/**
 * ✅ Email signup
 * ✅ RETURNS { user, isNewUser:true }
 */
export async function signUpWithEmail(email, password) {
  const res = await createUserWithEmailAndPassword(auth, email, password);

  // optional: send verification (your UI already handles verify)
  try {
    await sendEmailVerification(res.user);
  } catch {}

  return { user: res.user, isNewUser: true };
}

/**
 * ✅ Email login
 * ✅ RETURNS { user, isNewUser:false }
 */
export async function loginWithEmail(email, password) {
  const res = await signInWithEmailAndPassword(auth, email, password);
  return { user: res.user, isNewUser: false };
}

export async function logout() {
  await signOut(auth);
}

export async function resetPassword(email) {
  return await sendPasswordResetEmail(auth, email);
}

export async function resendVerificationEmail() {
  const u = auth.currentUser;
  if (!u) throw new Error("No current user");
  return await sendVerificationEmail(u);
}

// ✅ fix: keep helper name consistent
async function sendVerificationEmail(u) {
  return await sendEmailVerification(u);
}

export async function refreshUser() {
  const u = auth.currentUser;
  if (!u) throw new Error("No current user");
  await reload(u);
  return u;
}
